import AccountSummarySkeleton from './account-summary.skeleton'
import AutoPayIndicator from '../payments-page/components/auto-pay-indicator'
import { Button, SpendingProgressBar } from '@/components/index'
import { ExtendedTooltip } from '@/components'
import { Icons } from '@/theme/Images'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { formatDateOnly } from '@/utils/date-format-util'
import { transformAmount } from '@/utils/string-format-utils'
import { useAccountSummary } from '../hooks/use-account-summary'
import { useState } from 'react'
import { ROUTE_PAYMENTS, ROUTE_PAYMENTS_MAKE_PAYMENT } from '../../routes/paths'

// Define constants
const STATEMENTBALANCE = 'statement-balance'
const CURRUNTBALANCE = 'current-balance'
const MINIMUMPAYMENT = 'minimum-payment'

const AccountSummary = () => {
  const [isHovered, setIsHovered] = useState('')

  const { isFetching: isAccountSummaryLoading, data: accountSummaryData } = useAccountSummary()

  const summaryData = accountSummaryData?.currencyCode
    ? accountSummaryData
    : {
        currentBalance: 0,
        availableCredit: 0,
        creditLimit: 0,
        lastStatement: 0,
        currencyCode: 'USD',
        currentDue: 0,
        latestStatementBalance: 0,
        minimumPaymentDueHome: 0,
        dueDate: 'Not Available',
      }

  if (isAccountSummaryLoading) {
    return <AccountSummarySkeleton />
  }

  const renderStatementInfo = () => {
    const dueDateNotAvailable = summaryData?.dueDate === 'Not Available'
    return (
      <div className="flex flex-row justify-between gap-4 max-md:flex-col max-md:gap-1 w-full">
        <div
          className={`flex flex-col max-md:justify-between max-md:w-full ${dueDateNotAvailable ? 'max-md:flex-row' : 'max-md:flex-row-reverse'}`}
        >
          <p
            data-testid="balance-field"
            className={`text-neutral-900 ${dueDateNotAvailable ? 'font-normal text-sm max-md:text-neutral-700 md:font-semibold md:text-base' : 'font-semibold'} `}
          >
            {dueDateNotAvailable
              ? 'No payment due at this time'
              : `${formatDateOnly(summaryData?.dueDate, 'MMM dd, yyyy')}`}
          </p>
          {!dueDateNotAvailable && <p className="text-neutral-700 text-sm">Payment due date</p>}
        </div>

        <div className="flex flex-row gap-6">
          <div className="w-[1px] h-[45px] bg-neutral-200 max-md:hidden" />
          <div className="flex flex-col max-md:flex-row-reverse max-md:justify-between max-md:w-full">
            <p className="text-neutral-900 font-semibold">{transformAmount(summaryData.minimumPaymentDueHome || 0)}</p>
            <div className="flex items-center">
              <p className="text-neutral-700 text-sm">Minimum payment due</p>
              <ExtendedTooltip
                trigger={
                  <img
                    src={isHovered === MINIMUMPAYMENT ? Icons.helpBoldIcon : Icons.helpIcon}
                    className="cursor-pointer ml-2"
                    width={13.5}
                    onMouseEnter={() => setIsHovered(MINIMUMPAYMENT)}
                    onMouseLeave={() => setIsHovered('')}
                  />
                }
                title={'Minimum Payment'}
                content="The minimum amount you must pay by the due date to avoid late fees and maintain your account in good standing. Paying only the minimum may result in interest charges on the remaining balance."
              />
            </div>
          </div>
        </div>

        <div className="flex flex-row gap-6">
          <div className="w-[1px] h-[45px] bg-neutral-200 max-md:hidden" />
          <div className="flex flex-col max-md:flex-row-reverse max-md:justify-between max-md:w-full">
            <p data-testid="balance-field" className="text-neutral-900 font-semibold">
              {transformAmount(summaryData.latestStatementBalance) || '0.00'}
            </p>
            <div className="flex items-center">
              <p className="text-neutral-700 text-sm">Statement balance</p>
              <ExtendedTooltip
                trigger={
                  <img
                    src={isHovered === STATEMENTBALANCE ? Icons.helpBoldIcon : Icons.helpIcon}
                    className="cursor-pointer ml-2"
                    width={13.5}
                    onMouseEnter={() => setIsHovered(STATEMENTBALANCE)}
                    onMouseLeave={() => setIsHovered('')}
                  />
                }
                title={'Statements Balance'}
                content="The total balance on your account at the end of your last billing cycle. This amount does not reflect any payments made or applied to your balance after the statement was generated."
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div data-testid="right-column">
      <div className="top-content w-full border-b-dGray-200 border-b-[1px] px-6 py-8 flex flex-col gap-6">
        <div className="w-full flex flex-col md:flex-row md:flex-wrap justify-between gap-2">
          <div className="flex flex-col">
            <div className="flex flex-row justify-start items-center">
              <p className="font-inter font-semibold text-base leading-7 text-neutral-700 tracking-normal">
                Current Balance
              </p>
              <ExtendedTooltip
                trigger={
                  <img
                    src={isHovered === CURRUNTBALANCE ? Icons.helpBoldIcon : Icons.helpIcon}
                    className="cursor-pointer ml-2"
                    width={13.5}
                    onMouseEnter={() => setIsHovered(CURRUNTBALANCE)}
                    onMouseLeave={() => setIsHovered('')}
                  />
                }
                title={'Current Balance'}
                content="The total balance on your account, including all posted purchases, fees, and interest, minus any payments or credits. This amount updates as new purchases and payments are posted and may differ from your Statement Balance, which reflects the amount due for your last billing cycle."
              />
            </div>
            <p
              className="font-inter font-semibold text-[40px] mt-1 text-neutral-900 leading-11 tracking-tight"
              data-testid="balance-field"
            >
              {transformAmount(summaryData.currentBalance) || '0.00'}
            </p>
            <p className="font-inter font-normal text-sm leading-5 text-neutral-700 pt-1">
              Credit limit: {transformAmount(summaryData.creditLimit) || '0.00'}
            </p>
          </div>

          <div className="mt-4 w-full md:hidden">
            <SpendingProgressBar className="mb-4" />
            {renderStatementInfo()}
          </div>

          <div className="flex flex-col justify-start max-w-34 max-md:w-full pt-2 gap-2">
            <Link to={`/${ROUTE_PAYMENTS}${ROUTE_PAYMENTS_MAKE_PAYMENT}`} className="w-full mt-4 md:mt-0 mb-1">
              <Button
                id="home-page-make-payment-button"
                aria-label="Make Payment"
                className="w-full bg-brand-500 hover:bg-brand-700 text-sm text-white"
              >
                Make Payment
              </Button>
            </Link>
            <AutoPayIndicator />
          </div>

          <div className="mt-4 w-full max-md:hidden">{renderStatementInfo()}</div>
        </div>
      </div>
    </div>
  )
}

AccountSummary.propTypes = {
  data: PropTypes.object,
}

export default AccountSummary
